import { useLazyQuery, useQuery } from "@apollo/client";
import { IonIcon, IonItem, IonList, IonText } from "@ionic/react";
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { titleCase } from "title-case";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import Dot from "@components/Dot";
import ExpertInitialAvatar from "@components/ExpertInitialAvatar";
import styles from "@components/expertJourney/ExpertJourneyMenu.module.css";
import InviteExpertForm from "@components/forms/InviteExpertForm";
import HandleQuery from "@components/HandleQuery";
import Menu from "@components/menus/Menu";
import MenuItem from "@components/menus/MenuItem";
import menuItemStyles from "@components/menus/MenuItem.module.css";
import Modal from "@components/Modal";
import Nbsp from "@components/Nbsp";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import { sessionContext } from "@context/Contexts";
import useSubscribeToAllExperts from "@hooks/subscriptions/useSubscribeToAllExperts";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ClientAccessTypeEnum,
  ClientRoleRoleEnum,
  ExpertGlobalRoleEnum,
  ExpertRoleDestroyDocument,
  ExpertRoleDestroyMutation,
  ExpertRoleDestroyMutationVariables,
  ExpertRoleRoleEnum,
  GetClientWithJourneysDocument,
  GetClientWithJourneysQuery,
  GetClientWithJourneysQueryVariables,
  GetExpertsDocument,
  GetExpertsQuery,
  GetExpertsQueryVariables,
  GetExpertUnreadMessageCountDocument,
  GetExpertUnreadMessageCountQuery,
  GetExpertUnreadMessageCountQueryVariables,
  GetJourneyWithCareProfileAndEligibilityDocument,
  GetJourneyWithCareProfileAndEligibilityQuery,
  GetJourneyWithCareProfileAndEligibilityQueryVariables,
  JourneyCarePlanPublishDocument,
  JourneyCarePlanPublishMutation,
  JourneyCarePlanPublishMutationVariables,
  PackageGrantEligibilityStatusEnum
} from "@typing/Generated";
import { contractDetailsOnDate } from "@utils/employerContractUtils";
import { IconList, IconName } from "@utils/iconUtils";
import { expertJourneyMenuProps } from "@utils/menuUtils";
import { currentPlanBalance, getCareProfileName, sortByStringField, sortByTimestamp } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { minutesToHoursAndMinutes } from "@utils/timeUtils";

type ExpertRole = NonNullable<GetJourneyWithCareProfileAndEligibilityQuery["journey"]["expertRoles"]>[number];
type EmployerContract = NonNullable<NonNullable<GetClientWithJourneysQuery["client"]["employer"]>["currentContract"]>;

type ExpertMenuItemProps = {
  children?: ReactNode;
  href?: string;
  iconName?: IconName;
  onClick: () => void;
  routerLink?: string;
  textKey?: string;
  unread?: boolean;
};

const ExpertMenuItem = ({
  children,
  href,
  iconName,
  onClick,
  routerLink,
  textKey,
  unread = false
}: ExpertMenuItemProps) => {
  const intl = useIntl();

  return (
    <IonItem
      button
      className={styles.expertMenuItem}
      href={href}
      lines="none"
      onClick={onClick}
      rel={href ? "noopener,noreferrer" : ""}
      routerLink={routerLink}
    >
      {children ? (
        <>{children}</>
      ) : (
        <div className={styles.menuIcon}>
          {iconName && textKey && (
            <IonIcon
              aria-label={intl.formatMessage({ id: `menus.expert.${textKey}` })}
              src={IconList[iconName]}
              title={intl.formatMessage({ id: `menus.expert.${textKey}` })}
            />
          )}
        </div>
      )}
      {unread && (
        <div className={styles.unreadWrapper}>
          <div className={styles.unread}>
            <Dot unread={unread} />
          </div>
        </div>
      )}
    </IonItem>
  );
};

type LeftSectionProps = {
  closeMenu: () => void;
  handleAdminClick: () => void;
  hasUnreadMessages: boolean;
  isAdmin: boolean;
  managedExperts: NonNullable<NonNullable<NonNullable<GetExpertsQuery["experts"]["edges"]>[number]>["node"]>[];
};

const LeftSection = ({ closeMenu, handleAdminClick, hasUnreadMessages, isAdmin, managedExperts }: LeftSectionProps) => {
  const myExpert = useMyExpert();
  const myExpertGlobalRole = myExpert?.expertGlobalRole;

  const universalSearchOpener = useGlobalOpener(OpenerTypeEnum.UNIVERSAL_SEARCH);

  const openSearch = useCallback(() => {
    universalSearchOpener.open();
  }, [universalSearchOpener]);

  return (
    <IonList>
      <ExpertMenuItem iconName="search" onClick={openSearch} textKey="search" />
      <ExpertMenuItem
        iconName="columns"
        onClick={closeMenu}
        routerLink="/expert/dashboard"
        textKey="dashboard"
        unread={hasUnreadMessages}
      />
      <ExpertMenuItem iconName="clipboardListCheck" onClick={closeMenu} routerLink="/expert/tasks" textKey="allTasks" />
      <ExpertMenuItem iconName="calendar" onClick={closeMenu} routerLink="/expert/consults" textKey="allConsults" />
      <ExpertMenuItem iconName="history" onClick={closeMenu} routerLink="/expert/activities" textKey="allActivities" />
      <ExpertMenuItem
        iconName="handsHeart"
        onClick={closeMenu}
        routerLink="/expert/care-plans"
        textKey="allCarePlans"
      />
      <ExpertMenuItem iconName="users" onClick={closeMenu} routerLink="/expert/journeys" textKey="journeyList" />
      <ExpertMenuItem href="https://gather.withgrayce.com" iconName="handHeart" onClick={closeMenu} textKey="gather" />
      <ExpertMenuItem iconName="clock" onClick={closeMenu} routerLink="/expert/time-logs" textKey="timeLogs" />
      <ExpertMenuItem iconName="plane" onClick={closeMenu} routerLink="/expert/ooo-schedule" textKey="outOfOffice" />
      <ExpertMenuItem iconName="building" onClick={closeMenu} routerLink="/expert/employers" textKey="employers" />
      <ExpertMenuItem iconName="tags" onClick={closeMenu} routerLink="/expert/tags" textKey="tags" />

      {isAdmin && <ExpertMenuItem iconName="crown" onClick={handleAdminClick} textKey="admin" />}
      {!isAdmin && (
        <ExpertMenuItem iconName="arrowRightFromBracket" onClick={closeMenu} routerLink="/sign-out" textKey="signOut" />
      )}
      {myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER && (
        <>
          <hr className={styles.divider} />
          <ExpertMenuItem
            iconName="usersCrown"
            onClick={closeMenu}
            routerLink="/expert/all-journeys"
            textKey="allJourneyList"
          />
          <ExpertMenuItem iconName="envelope" onClick={closeMenu} routerLink="/expert/messages" textKey="messageList" />
          <ExpertMenuItem
            iconName="bullseyeArrow"
            onClick={closeMenu}
            routerLink="/expert/goal-templates"
            textKey="goalList"
          />
          <ExpertMenuItem
            iconName="bookCopy"
            onClick={closeMenu}
            routerLink="/expert/resource-feature-templates"
            textKey="resourceFeatureTemplates"
          />
          <ExpertMenuItem
            iconName="tableLayout"
            onClick={closeMenu}
            routerLink="/expert/message-templates"
            textKey="messageTemplates"
          />
          <ExpertMenuItem iconName="tags" onClick={closeMenu} routerLink="/expert/tags" textKey="manageTags" />
          <ExpertMenuItem
            iconName="clipboardUser"
            onClick={closeMenu}
            routerLink="/expert/manage-experts"
            textKey="expertList"
          />
          {managedExperts.map(managedExpert => (
            <ExpertMenuItem
              key={managedExpert.id}
              onClick={closeMenu}
              routerLink={`/expert/manage-experts/${managedExpert.id}/journeys`}
            >
              <ExpertInitialAvatar
                expert={managedExpert}
                size="tiny"
                title={`${managedExpert.fullName}'s Journey List`}
              />
            </ExpertMenuItem>
          ))}
        </>
      )}
    </IonList>
  );
};

type TeamMemberProps = {
  canDelete: boolean;
  onDelete: (role: ExpertRole) => void;
  role: ExpertRole;
};

const TeamMember = ({ canDelete, onDelete, role }: TeamMemberProps) => {
  const deleteConfirmationOpener = useOpener();

  return (
    <div className={styles.supportingExpert} key={role.id}>
      <IonText>{role.expert.firstName}</IonText>
      {canDelete && (
        <div>
          <CircleButton
            ariaLabelKey="accessibility.ariaLabels.remove"
            color="white"
            fill="clear"
            icon="userTimes"
            onClick={deleteConfirmationOpener.open}
            size="medium"
          />
        </div>
      )}
      <DeleteConfirmationAlert
        messageKey="menus.expertJourney.reallyDeleteExpert"
        messageValue={role.expert.firstName}
        messageValueKey="name"
        onDelete={() => onDelete(role)}
        opener={deleteConfirmationOpener}
      />
    </div>
  );
};

type Props = {
  journeyId: string;
};

const ExpertJourneyMenu = ({ journeyId }: Props) => {
  const intl = useIntl();
  const inviteExpertOpener = useOpener();
  const menuRef = useRef<HTMLIonMenuElement>(null);
  const history = useHistory();
  const myExpert = useMyExpert();
  const myExpertGlobalRole = myExpert?.expertGlobalRole;
  const timeTrackingOpener = useGlobalOpener(OpenerTypeEnum.TIME_LOG);

  const { sessionState } = useContext(sessionContext);
  const isAdmin = sessionState.impersonatingExpert;

  const [carePlanPublish] = useMutationWithValidation<
    JourneyCarePlanPublishMutation,
    JourneyCarePlanPublishMutationVariables
  >(JourneyCarePlanPublishDocument, "carePlan");

  const publishCarePlan = useCallback(() => {
    carePlanPublish({
      id: journeyId
    }).then(() => {
      timeTrackingOpener.open();
    });
  }, [carePlanPublish, journeyId, timeTrackingOpener]);

  const getJourneyQuery = useQuery<
    GetJourneyWithCareProfileAndEligibilityQuery,
    GetJourneyWithCareProfileAndEligibilityQueryVariables
  >(GetJourneyWithCareProfileAndEligibilityDocument, {
    variables: { id: journeyId }
  });

  const [getClient, getClientQuery] = useLazyQuery<GetClientWithJourneysQuery, GetClientWithJourneysQueryVariables>(
    GetClientWithJourneysDocument
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
    getClientQuery.refetch();
  });

  const [getExperts, getExpertsQuery] = useLazyQuery<GetExpertsQuery, GetExpertsQueryVariables>(GetExpertsDocument);

  const [getMessageCount, getMessageCountQuery] = useLazyQuery<
    GetExpertUnreadMessageCountQuery,
    GetExpertUnreadMessageCountQueryVariables
  >(GetExpertUnreadMessageCountDocument);

  useEffect(() => {
    if (myExpert?.id) {
      getMessageCount({
        variables: {
          expertId: myExpert.id
        }
      });
    }
  }, [getMessageCount, myExpert?.id]);

  useSubscribeToAllExperts(
    () => {
      if (myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER) {
        getExpertsQuery.refetch();
      }
    },
    { skip: myExpertGlobalRole !== ExpertGlobalRoleEnum.MANAGER }
  );

  useSubscribeToJourneys(() => {
    getMessageCountQuery.refetch();
  });

  useEffect(() => {
    if (myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER) {
      getExperts();
    }
  }, [getExperts, myExpert, myExpertGlobalRole]);

  useEffect(() => {
    if (getExpertsQuery.data?.experts.pageInfo.hasNextPage) {
      getExpertsQuery.fetchMore({ variables: { cursor: getExpertsQuery.data.experts.pageInfo.endCursor ?? "" } });
    }
  }, [getExpertsQuery]);

  const hasUnreadMessages = useMemo(() => {
    if (getMessageCountQuery.data?.expertSummary && getMessageCountQuery.data.expertSummary.unreadMessages > 0) {
      return true;
    }
    return false;
  }, [getMessageCountQuery.data?.expertSummary]);

  const allExperts = useNodesFromConnection(getExpertsQuery.data?.experts);
  const managedExperts = useMemo(() => {
    const managed = allExperts.filter(expert => expert.globalRole === ExpertGlobalRoleEnum.REGULAR);
    return sortByStringField(managed, expert => expert.lastName);
  }, [allExperts]);

  const closeMenu = useCallback(() => {
    if (menuRef.current) {
      menuRef.current.close();
    }
  }, [menuRef]);
  const handleAdminClick = useCallback(() => {
    closeMenu();
    history.push("/admin/end-impersonation");
  }, [closeMenu, history]);

  const journeyExpertRoles = useMemo(() => {
    const primary = getJourneyQuery.data?.journey.expertRoles?.find(role => role.role === ExpertRoleRoleEnum.PRIMARY);
    const teamMembers = getJourneyQuery.data?.journey.expertRoles?.filter(
      role => role.role === ExpertRoleRoleEnum.TEAM_MEMBER
    );

    return {
      primary,
      teamMembers
    };
  }, [getJourneyQuery.data?.journey.expertRoles]);

  const currentExpertRole = useMemo(
    () => getJourneyQuery.data?.journey.expertRoles?.find(role => role.expert.id === myExpert?.id),
    [getJourneyQuery.data?.journey.expertRoles, myExpert?.id]
  );

  const [destroyExpertRole, client] = useMutationWithValidation<
    ExpertRoleDestroyMutation,
    ExpertRoleDestroyMutationVariables
  >(ExpertRoleDestroyDocument, "expertRole", {
    update: (_store, { data }) => {
      if (data?.expertRoleDestroy) {
        client.resetStore();
      }
    }
  });

  const handleExpertRoleDestroy = useCallback(
    (expertRole: ExpertRole) => {
      if (expertRole) {
        destroyExpertRole({
          id: expertRole.id
        });
      }
    },
    [destroyExpertRole]
  );

  const handleTeamMemberRoleDestroy = useCallback(() => {
    if (currentExpertRole) {
      destroyExpertRole({
        id: currentExpertRole.id
      }).then(() => {
        history.push(`/expert/journeys`);
      });
    }
  }, [currentExpertRole, destroyExpertRole, history]);

  const canCreateRoles = useMemo(
    () => myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER || currentExpertRole?.role === ExpertRoleRoleEnum.PRIMARY,
    [currentExpertRole?.role, myExpertGlobalRole]
  );

  useEffect(() => {
    if (getJourneyQuery.data?.journey.owner.id) {
      getClient({ variables: { id: getJourneyQuery.data.journey.owner.id } });
    }
  }, [getClient, getJourneyQuery.data?.journey.owner.id]);

  const canDestroyRole = useCallback(
    (role: ExpertRole) => {
      if (
        role.expert.globalRole === ExpertGlobalRoleEnum.MANAGER &&
        myExpertGlobalRole !== ExpertGlobalRoleEnum.MANAGER
      ) {
        return false;
      }
      if (role.expert.id === myExpert?.id) {
        return false;
      }
      return (
        myExpertGlobalRole === ExpertGlobalRoleEnum.MANAGER || currentExpertRole?.role === ExpertRoleRoleEnum.PRIMARY
      );
    },
    [currentExpertRole?.role, myExpert?.id, myExpertGlobalRole]
  );

  const earliestExpiringPackage = useMemo(() => {
    const grants = getJourneyQuery.data?.journey.owner.eligiblePackageGrants;
    if (grants) {
      const unexpired = grants.filter(grant => !grant.expired && grant.expiresAt !== null);
      return sortByTimestamp(unexpired, grant => grant.expiresAt as Date).reverse()[0];
    }
  }, [getJourneyQuery.data?.journey.owner.eligiblePackageGrants]);
  const menuHidden = useMemo(() => {
    const hiddenMenuRoutes = ["/schedule-consult"];

    if (hiddenMenuRoutes.some(route => history.location.pathname.includes(route))) {
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname, history.location.search]);

  const renderContractHoursOnDate = useCallback((contract: EmployerContract, date: Date, isRefresh = false) => {
    const contractDetails = contractDetailsOnDate(contract, date);

    let minutes = contractDetails.minutes;

    if (contractDetails.hasCarePlanning && isRefresh) {
      minutes = minutes + 120;
    }

    return (
      <>
        {contractDetails.hasUnlimitedMinutes ? (
          <FormattedMessage id="dictionary.unlimited" />
        ) : (
          <>
            {minutesToHoursAndMinutes({
              dropZeroMinutes: true,
              minutes
            })}
            <Nbsp />
            <FormattedMessage id="dictionary.hoursLower" />
          </>
        )}
      </>
    );
  }, []);

  const renderNextPossibleRefresh = useCallback(() => {
    const packageGrantInCurrentRefreshPeriod = getClientQuery.data?.client.packageGrants.find(
      grant => grant.refreshPeriod?.id === getClientQuery.data?.client.employer?.currentRefreshPeriod?.id
    );

    // If they haven't gotten one in this refresh period, report that they can have one now.
    if (!packageGrantInCurrentRefreshPeriod) {
      return (
        <>
          <FormattedMessage id="menus.expertJourney.refreshAvailableNow" />
          {getClientQuery.data?.client.employer?.currentContract && (
            <>{renderContractHoursOnDate(getClientQuery.data.client.employer.currentContract, new Date(), true)}</>
          )}
        </>
      );
    }

    // If they got one in the current period and it's waiting for work email verification
    // then the member can verify their email to get the package.
    if (
      packageGrantInCurrentRefreshPeriod?.eligibilityStatus ===
        PackageGrantEligibilityStatusEnum.AWAITING_WORK_EMAIL_VERIFICATION &&
      getClientQuery.data?.client.employer
    ) {
      return (
        <>
          <FormattedMessage id="menus.expertJourney.refreshPendingWorkEmailVerification" />
          {getClientQuery.data.client.employer.currentContract && (
            <>{renderContractHoursOnDate(getClientQuery.data.client.employer.currentContract, new Date(), true)}</>
          )}
        </>
      );
    }

    // Otherwise, they can't get a package in the current period and need to wait until the next one.
    if (
      getClientQuery.data?.client.employer?.nextRefreshPeriod?.startDate &&
      getClientQuery.data.client.employer.currentContract
    ) {
      return (
        <>
          <DateStamp value={getClientQuery.data?.client.employer?.nextRefreshPeriod?.startDate} />
          <br />
          {renderContractHoursOnDate(
            getClientQuery.data.client.employer.currentContract,
            getClientQuery.data.client.employer.nextRefreshPeriod.startDate,
            true
          )}
        </>
      );
    }

    return <FormattedMessage id="dictionary.none" />;
  }, [getClientQuery.data?.client.employer, getClientQuery.data?.client.packageGrants, renderContractHoursOnDate]);

  return (
    <Menu
      hidden={menuHidden}
      journeyId={journeyId}
      leftSection={
        <LeftSection
          closeMenu={closeMenu}
          handleAdminClick={handleAdminClick}
          hasUnreadMessages={hasUnreadMessages}
          isAdmin={isAdmin}
          managedExperts={managedExperts}
        />
      }
      menuRef={menuRef}
      mobileHidden
    >
      <HandleQuery query={getJourneyQuery}>
        {data => (
          <>
            {!data.journey.carePlanPublishedAt && (
              <div className={[styles.publishButton, menuItemStyles.menuItem].join(" ")}>
                <Button color="primary-300" expand="block" onClick={publishCarePlan} size="large">
                  <FormattedMessage id="pages.carePlan.base.submit" />
                </Button>
              </div>
            )}
            {expertJourneyMenuProps(intl, journeyId, myExpert, data.journey).map(prop => (
              <MenuItem key={prop.text} {...prop} />
            ))}
            <div className={styles.extraMenuInfo}>
              <dl className={styles.careTeamInfo}>
                <dt>{intl.formatMessage({ id: "menus.expertJourney.lovedOne" })}</dt>
                <dd>{getCareProfileName({ intl, journey: data.journey })}</dd>
                <dt>{intl.formatMessage({ id: "menus.expertJourney.accountOwner" })}</dt>
                {data.journey.clientRoles
                  .filter(clientRole => clientRole.role === ClientRoleRoleEnum.OWNER)
                  .map(clientRole => (
                    <dd key={clientRole.id}>
                      <div>{clientRole.client.fullName}</div>
                      <div className={styles.italic}>
                        {clientRole.relationship?.relationshipOption &&
                          (clientRole.relationship?.other
                            ? intl.formatMessage(
                                {
                                  id: `models.clientRole.relationshipInverseWithoutName.otherWithValue`
                                },
                                { value: titleCase(clientRole.relationship.other) }
                              )
                            : intl.formatMessage({
                                id: `models.clientRole.relationshipInverseWithoutName.${clientRole.relationship.relationshipOption}`
                              }))}
                      </div>
                    </dd>
                  ))}
                {data.journey.clientRoles.length > 1 && (
                  <dt>{intl.formatMessage({ id: "menus.expertJourney.additionalParticipants" })}</dt>
                )}
                {data.journey.clientRoles
                  .filter(clientRole => clientRole.role !== ClientRoleRoleEnum.OWNER)
                  .map(clientRole => (
                    <dd key={clientRole.id}>
                      <div>{clientRole.client.fullName}</div>
                      <div className={styles.italic}>
                        {clientRole.relationship?.relationshipOption &&
                          (clientRole.relationship?.other
                            ? intl.formatMessage(
                                {
                                  id: `models.clientRole.relationshipInverseWithoutName.otherWithValue`
                                },
                                { value: titleCase(clientRole.relationship.other) }
                              )
                            : intl.formatMessage({
                                id: `models.clientRole.relationshipInverseWithoutName.${clientRole.relationship.relationshipOption}`
                              }))}
                      </div>
                    </dd>
                  ))}
                <dt>
                  <FormattedMessage id="menus.expertJourney.employer" />
                </dt>
                {data.journey.owner.accessType === ClientAccessTypeEnum.EMPLOYER_SPONSORED ? (
                  <>
                    {data?.journey.owner.employer ? (
                      <dd>
                        <InternalLink
                          color="white"
                          to={journeyRoute({
                            journeyId,
                            linkAfterJourney: `/employers/${data.journey.owner.employer.id}`,
                            myExpert
                          })}
                        >
                          {data.journey.owner.employer.name}
                        </InternalLink>
                      </dd>
                    ) : (
                      <dd>{intl.formatMessage({ id: "menus.expertJourney.employerUnknown" })}</dd>
                    )}
                  </>
                ) : (
                  <dd>{intl.formatMessage({ id: "dictionary.selfPaid" })}</dd>
                )}
                <div className="ion-margin-top">
                  <dt>
                    <FormattedMessage id="menus.expertJourney.expertOwner" />
                  </dt>
                  <dd>{journeyExpertRoles.primary?.expert.firstName}</dd>
                </div>
                <dt>
                  <FormattedMessage id="menus.expertJourney.supportingExperts" />
                </dt>
                {journeyExpertRoles.teamMembers?.map(role => (
                  <TeamMember
                    canDelete={canDestroyRole(role)}
                    key={role.id}
                    onDelete={handleExpertRoleDestroy}
                    role={role}
                  />
                ))}
                {canCreateRoles && (
                  <dd className={styles.inviteMargin}>
                    <ButtonLink className={styles.inviteExpert} color="white" onClick={inviteExpertOpener.open}>
                      <FormattedMessage id="menus.expertJourney.inviteExpert" />
                    </ButtonLink>
                  </dd>
                )}
                {currentExpertRole?.role === ExpertRoleRoleEnum.TEAM_MEMBER && (
                  <div className="ion-margin-vertical">
                    <Button color="secondary-500" onClick={handleTeamMemberRoleDestroy}>
                      <FormattedMessage id="menus.expertJourney.leaveJourney" />
                    </Button>
                  </div>
                )}
              </dl>
              <dl className={styles.packageInfo}>
                <dt>
                  <FormattedMessage id="menus.expertJourney.hourBalance" />
                </dt>
                <dd>{currentPlanBalance(data.journey.owner, intl, data.journey)}</dd>
                <dt>
                  <FormattedMessage id="menus.expertJourney.earliestExpiration" />
                </dt>
                <dd>
                  {earliestExpiringPackage?.expiresAt ? (
                    <>
                      <DateStamp value={earliestExpiringPackage?.expiresAt} />
                      <br />
                      {earliestExpiringPackage.hasUnlimitedMinutes ? (
                        <FormattedMessage id="dictionary.unlimited" />
                      ) : (
                        <>
                          {earliestExpiringPackage.hasCarePlanning &&
                            !earliestExpiringPackage.carePlanningCompleted && (
                              <>
                                <FormattedMessage id="menus.expertJourney.carePlanningPlus" />
                                <Nbsp />
                              </>
                            )}
                          {minutesToHoursAndMinutes({
                            dropZeroMinutes: true,
                            minutes: earliestExpiringPackage.minutesRemaining
                          })}
                          <Nbsp />
                          <FormattedMessage id="dictionary.hours" />
                        </>
                      )}
                    </>
                  ) : (
                    <FormattedMessage id="dictionary.none" />
                  )}
                </dd>
                <dt>
                  <FormattedMessage id="menus.expertJourney.nextPossibleRefresh" />
                </dt>
                <dd>{renderNextPossibleRefresh()}</dd>
              </dl>

              <Modal
                opener={inviteExpertOpener}
                title={intl.formatMessage({ id: "menus.expertJourney.inviteSupporting" })}
              >
                {() => (
                  <InviteExpertForm
                    expertRoles={data.journey.expertRoles}
                    journeyId={journeyId}
                    onCancel={inviteExpertOpener.close}
                    onSuccess={inviteExpertOpener.close}
                  />
                )}
              </Modal>
            </div>
          </>
        )}
      </HandleQuery>
    </Menu>
  );
};

export default ExpertJourneyMenu;
